import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Form, Input, Alert, List, Typography, Spin, Modal, Button } from 'antd';
import { useNotify } from '@/hooks';
import PrivateAction from '@/routes/PrivateAction';
import { API } from '@/services/api.service';
import AlertBox from '../alert-box';
import { Dialog, DialogTitle } from '../dialog/Dialog';

const { Text } = Typography;

export default function EmployeeBulkActionModal({
  action,
  open,
  queryKey,
  onClose,
  onRemove,
  selectedRowKeys,
  setQueryParams: _setQueryParams,
}) {
  const { t } = useTranslation(['menus', 'forms', 'messages']);

  const notification = useNotify();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [result, setResult] = useState(null);

  const initialValues = {
    selectedRowKeys,
  };

  useEffect(() => {
    if (open && selectedRowKeys && selectedRowKeys.length > 0) {
      form.setFieldsValue({
        selectedRowKeys,
      });
    }
  }, [open, selectedRowKeys, form]);

  useEffect(() => {
    if (!open) {
      setResult(null);
    }
  }, [open]);

  const handleClose = () => {
    onClose && onClose();
    onRemove && onRemove();
    setResult(null);
  };

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (selectedIds) => {
      switch (action) {
        case 'sendInvitations':
          return await API.employees.sendBulkInvitations(selectedIds);
        default:
          return null;
      }
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries([queryKey]);

      if (response?.successCount > 0) {
        setResult(response);
        notification.success({
          message: t('success', { ns: 'messages' }),
          description: t('invitationsSentSuccess', {
            count: response.successCount,
            ns: 'forms',
          }),
        });
      } else {
        setResult(response);
      }
    },
    onError: (error) => {
      notification.error({
        message: t('error', { ns: 'messages' }),
        description: error.response?.data?.message || error.message,
      });
    },
  });

  const onFinish = async (values) => {
    await mutateAsync(values.selectedRowKeys);
  };

  const alertOptions = useMemo(() => {
    if (!action) return;

    if (action === 'sendInvitations') {
      return {
        variant: 'info',
        text: t('sendInvitationsDescription', {
          count: selectedRowKeys?.length || 0,
          ns: 'forms',
        }),
      };
    }
  }, [action, t, selectedRowKeys?.length]);

  const renderResultContent = () => {
    if (!result) return null;

    return (
      <div className="space-y-4">
        {result.successCount > 0 && (
          <Alert
            type="success"
            message={t('invitationsSentSuccessCount', {
              count: result.successCount,
              ns: 'forms',
            })}
            showIcon
          />
        )}

        {result.skippedCount > 0 && (
          <Alert
            type="warning"
            message={t('invitationsSkippedCount', {
              count: result.skippedCount,
              ns: 'forms',
            })}
            description={
              <List
                size="small"
                dataSource={result.skipped}
                renderItem={(item) => (
                  <List.Item>
                    <Text>{item.name}</Text>
                    <Text type="secondary" className="ml-2">
                      - {t('companyNoEmployeeAccess', { ns: 'forms' })}
                    </Text>
                  </List.Item>
                )}
              />
            }
            showIcon
          />
        )}

        {result.failedCount > 0 && (
          <Alert
            type="error"
            message={t('invitationsFailedCount', {
              count: result.failedCount,
              ns: 'forms',
            })}
            description={
              <List
                size="small"
                dataSource={result.failed}
                renderItem={(item) => (
                  <List.Item>
                    <Text>{item.name}</Text>
                    <Text type="secondary" className="ml-2">
                      - {item.reason}
                    </Text>
                  </List.Item>
                )}
              />
            }
            showIcon
          />
        )}
      </div>
    );
  };

  // When showing results, use a simple Modal with custom footer
  if (result) {
    return (
      <PrivateAction
        requiredRoles={['superAdmin', 'admin', 'hrOfficer']}
        requiredPermissions={['employee_edit']}
      >
        <Modal
          title={t(action, { ns: 'forms' })}
          open={open}
          onCancel={handleClose}
          footer={
            <Button
              type="primary"
              onClick={handleClose}
              className="btn-danger inline-flex"
            >
              {t('close', { ns: 'forms' })}
            </Button>
          }
        >
          {renderResultContent()}
        </Modal>
      </PrivateAction>
    );
  }

  return (
    <PrivateAction
      requiredRoles={['superAdmin', 'admin', 'hrOfficer']}
      requiredPermissions={['employee_edit']}
    >
      <Dialog
        title={t(action, { ns: 'forms' })}
        open={open}
        onCancel={handleClose}
        form={form}
        confirmLoading={isPending}
      >
        {isPending ? (
          <div className="flex items-center justify-center py-8">
            <Spin size="large" />
            <Text className="ml-4">{t('sendingInvitations', { ns: 'forms' })}</Text>
          </div>
        ) : (
          <Form
            form={form}
            name="employee_bulk_action_form"
            onFinish={onFinish}
            layout="vertical"
            initialValues={initialValues}
          >
            <AlertBox variant={alertOptions?.variant} className="p-2 text-center">
              <span>{alertOptions?.text}</span>
            </AlertBox>

            <Form.Item name="selectedRowKeys" hidden>
              <Input type="hidden" />
            </Form.Item>

            <DialogTitle text={t(`${action}Text`, { ns: 'forms' })} />
          </Form>
        )}
      </Dialog>
    </PrivateAction>
  );
}
